// LeadAgentAcceptance.service.ts owns the HTTP calls for the Lead Agent
// pre-acceptance flow (RET-1230). Endpoints are bff-web proxies to
// workspace-service — see bff-web `docs/lead-agent-endpoints-from-bff-web.md`.

import { AxiosService } from 'src/axios/axios.service';
import {
  parseLeadAgentAcceptance,
  parseLeadAgentReactivatePreview,
  parseLeadAgentReactivateResult,
  parseLeadAgentTierChangePreview,
  parseLeadAgentTierChangeResult,
} from './LeadAgentAcceptance.functions';
import type {
  tChangeTierOptions,
  tGetReactivatePreviewOptions,
  tGetTierChangePreviewOptions,
  tLeadAgentAcceptance,
  tLeadAgentAcceptanceEnvelope,
  tLeadAgentReactivatePreview,
  tLeadAgentReactivateResult,
  tLeadAgentTierChangePreview,
  tLeadAgentTierChangeResult,
  tReactivateLeadAgentOptions,
  tTransitionLeadAgentStateOptions,
} from './LeadAgentAcceptance.types';

export const LeadAgentAcceptanceService = Object.freeze({
  /**
   * GET /workspace/{wid}/lead-agent/acceptance
   *
   * 404 is the CONTRACTUAL "banner hidden" answer (workspace not in the
   * eligible cohort, or state ACCEPTED/NOT_IN_PRESALE) — not a failure.
   * `validateStatus` lets 404 resolve instead of throwing so AxiosService
   * does not log it as an error or capture it to Sentry for the ~98% of
   * workspaces that are simply not in the presale cohort.
   */
  getAcceptance: async function (
    workspaceId: string,
  ): Promise<tLeadAgentAcceptance | null> {
    const response = await AxiosService.get<tLeadAgentAcceptanceEnvelope>(
      `workspace/${workspaceId}/lead-agent/acceptance`,
      {
        isBFFService: true,
        validateStatus: (status: number) =>
          (status >= 200 && status < 300) || status === 404,
      },
    );

    if (!response?.success || !response.data) return null;
    return parseLeadAgentAcceptance(response.data);
  },

  /**
   * PATCH /workspace/{wid}/lead-agent/acceptance/state
   *
   * Body: `{ state, tier? }` — `tier` only alongside `state: 'ACCEPTED'`
   * (upstream enforces with 422). Illegal state-machine transitions surface
   * as 409 (forwarded by the BFF, not masked as 424). Both reject so the
   * caller can react; they are real faults worth logging, unlike the GET 404.
   */
  transitionState: async function (
    workspaceId: string,
    options: tTransitionLeadAgentStateOptions,
  ): Promise<tLeadAgentAcceptance> {
    const { state, tier } = options;
    const response = await AxiosService.patch<
      tTransitionLeadAgentStateOptions,
      tLeadAgentAcceptanceEnvelope
    >(
      `workspace/${workspaceId}/lead-agent/acceptance/state`,
      { state, ...(tier ? { tier } : {}) },
      { isBFFService: true },
    );

    const acceptance = parseLeadAgentAcceptance(response?.data);
    if (!acceptance) {
      throw new Error(
        'lead-agent/acceptance/state returned an unparseable payload',
      );
    }
    return acceptance;
  },

  /**
   * POST /workspace/{wid}/lead-agent/acceptance/dismiss  (A10 / RET-1345)
   *
   * Persists the "vencido" banner dismiss (sets `dismissed_at`) so it stays
   * hidden across sessions/devices. Banner-only: no state change, no event,
   * idempotent set-once. 404 (no acceptance row) is the contractual no-op —
   * `validateStatus` lets it resolve instead of throwing, mirroring
   * `getAcceptance`. Episode-scoped upstream: a later transition clears it.
   */
  dismissAcceptance: async function (workspaceId: string): Promise<void> {
    await AxiosService.post<undefined, tLeadAgentAcceptanceEnvelope>(
      `workspace/${workspaceId}/lead-agent/acceptance/dismiss`,
      undefined,
      {
        isBFFService: true,
        validateStatus: (status: number) =>
          (status >= 200 && status < 300) || status === 404,
      },
    );
  },

  /**
   * GET /workspace/{wid}/lead-agent/reactivate/preview?tier=…  (A11 / RET-1343)
   *
   * The exact immediate-charge amount + `prorationDate` for reactivating the
   * Lead Agent at `tier`. Unlike `getAcceptance`, a 4xx here is a real fault
   * (422 already-active / unsupported plan, 424 transient comms) so it is left
   * to reject — the modal fails CLOSED on any error (confirm disabled + retry).
   * The `signal` aborts a stale preview when the Owner switches tier fast. An
   * unparseable 2xx body throws so we never hand a fabricated amount to confirm.
   */
  getReactivatePreview: async function (
    workspaceId: string,
    options: tGetReactivatePreviewOptions,
  ): Promise<tLeadAgentReactivatePreview> {
    const { tier, signal } = options;
    const response = await AxiosService.get<tLeadAgentAcceptanceEnvelope>(
      `workspace/${workspaceId}/lead-agent/reactivate/preview?tier=${tier}`,
      { isBFFService: true, signal },
    );

    const preview = parseLeadAgentReactivatePreview(response?.data);
    if (!preview) {
      throw new Error(
        'lead-agent/reactivate/preview returned an unparseable payload',
      );
    }
    return preview;
  },

  /**
   * POST /workspace/{wid}/lead-agent/reactivate  (A11 / RET-1343)
   *
   * Body `{ tier, prorationDate }` — `prorationDate` (unix seconds) MUST be the
   * value ECHOED from the preview so the charged amount matches and the backend
   * idempotency key (sub+price+prorationDate) collapses a retry instead of
   * double-charging. A 200 may still carry `status: 'failed'` (a payment
   * decline — retryable); 4xx (422 already-active / send_invoice) and 424
   * (transient) reject. An unparseable 2xx body throws.
   */
  reactivateLeadAgent: async function (
    workspaceId: string,
    options: tReactivateLeadAgentOptions,
  ): Promise<tLeadAgentReactivateResult> {
    const { tier, prorationDate } = options;
    const response = await AxiosService.post<
      tReactivateLeadAgentOptions,
      tLeadAgentAcceptanceEnvelope
    >(
      `workspace/${workspaceId}/lead-agent/reactivate`,
      { tier, prorationDate },
      { isBFFService: true },
    );

    const result = parseLeadAgentReactivateResult(response?.data);
    if (!result) {
      throw new Error(
        'lead-agent/reactivate returned an unparseable payload',
      );
    }
    return result;
  },

  /**
   * GET /workspace/{wid}/lead-agent/tier-change/preview?tier=…  (A8 / RET-1614)
   *
   * The exact immediate prorated charge + `prorationDate` for upgrading the Lead
   * Agent to a HIGHER `tier`. Same fail-CLOSED contract as the reactivate
   * preview: a 4xx (422 not-an-upgrade / unsupported) or 424 (transient) is a
   * real fault left to reject, and an unparseable 2xx body throws — the modal
   * never confirms against a fabricated amount. `signal` aborts a stale preview
   * when the Owner switches tier fast.
   */
  getTierChangePreview: async function (
    workspaceId: string,
    options: tGetTierChangePreviewOptions,
  ): Promise<tLeadAgentTierChangePreview> {
    const { tier, signal } = options;
    const response = await AxiosService.get<tLeadAgentAcceptanceEnvelope>(
      `workspace/${workspaceId}/lead-agent/tier-change/preview?tier=${tier}`,
      { isBFFService: true, signal },
    );

    const preview = parseLeadAgentTierChangePreview(response?.data);
    if (!preview) {
      throw new Error(
        'lead-agent/tier-change/preview returned an unparseable payload',
      );
    }
    return preview;
  },

  /**
   * POST /workspace/{wid}/lead-agent/tier-change  (A8 / RET-1614)
   *
   * Body `{ tier, prorationDate }` — `prorationDate` (unix seconds) MUST be the
   * value ECHOED from the preview so the charged amount matches and the backend
   * idempotency key (sub+price+prorationDate) collapses a retry instead of
   * double-charging. A 200 may still carry `status: 'failed'` (a payment
   * decline — retryable) or `status: 'requires_action'` (3DS, confirmed
   * client-side); 4xx (422 not-an-upgrade) and 424 (transient) reject. An
   * unparseable 2xx body throws.
   */
  changeTier: async function (
    workspaceId: string,
    options: tChangeTierOptions,
  ): Promise<tLeadAgentTierChangeResult> {
    const { tier, prorationDate } = options;
    const response = await AxiosService.post<
      tChangeTierOptions,
      tLeadAgentAcceptanceEnvelope
    >(
      `workspace/${workspaceId}/lead-agent/tier-change`,
      { tier, prorationDate },
      { isBFFService: true },
    );

    const result = parseLeadAgentTierChangeResult(response?.data);
    if (!result) {
      throw new Error(
        'lead-agent/tier-change returned an unparseable payload',
      );
    }
    return result;
  },
});
